import React, { Component } from 'react'
import { AiOutlineSearch, AiOutlineWarning, AiOutlineDown, AiOutlineRight, AiOutlineCheckCircle, AiOutlineClose } from "react-icons/ai"
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import { NumberFormat } from '../../functions/generalFunctions'
import { pricesFormat } from '../../functions/pricesFormat.js'
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"

const LOW_MARGIN_THRESHOLD = 20

// Inline button styles — this page's own .actionBtn/.btnPrimaryCustom lookalikes
// turned out to live in OTHER pages' CSS Modules (AddNewProduct/ShowCustomerV2),
// not globally available here, so buttons rendered unstyled. Using the design
// token colors directly avoids depending on another page's scoped CSS.
const BTN_PRIMARY = { background: '#2563EB', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }
const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }

/**
 * Price Management — admin UI for the standardized pricing project (2026-08).
 *
 * Flow: browse the product catalog (union of itemCatalogue + itemCatalogueAltitude
 * — the same tables Sales Order's catalogue reads, confirmed 2026-08-13), click a product to
 * expand it, and fill in one row per company underneath — always inputs
 * (pre-filled if a product_pricing row already exists, empty if not), each with
 * its own Save that upserts just that company. No separate create/edit modal —
 * expanding a product IS the management UI.
 *
 * Completeness across companies is shown TWICE, same visual language both times
 * (icon + color), per the 2026-08-13 feedback that a faint row tint alone isn't
 * enough to scan a long list:
 *   - on the collapsed product row itself (icon + label, so nobody has to open
 *     every product just to check)
 *   - on each expanded company sub-row (already existed)
 * States: none (red) / partial (amber) / full (green-ish neutral).
 *
 * Only companies with company.status = 1 get a sub-row — status (not
 * systemCompanyStatus) is the real active flag, confirmed with the user
 * 2026-08-13 after a deactivated company still showed up.
 *
 * Only TypeUser === '1' can edit; everyone else sees the catalog + prices
 * read-only (expand still works, inputs are disabled). Every save is written
 * server-side to product_pricing_history for audit.
 *
 * Tiered customer pricing (2026-08-19): the old single "Standard" price per
 * company is now 3 tiers (Level 1/2/3) — customers get assigned a tier on the
 * new Customer Price Levels page, and Sales Order V3 prices the cart from
 * whichever tier the selected customer has (default Level 1). The old
 * standardPrice column is still saved unchanged in the background (not shown
 * here) so getMap/getCatalog can keep falling back to it for any item whose
 * levels aren't filled in yet — see productPricing.ts's upsert/getMap and
 * migrations/015_product_pricing_levels.sql.
 *
 * Active/Inactive filter (2026-08-19): client reported both showing together
 * with no way to separate them. Backed by MAS90's InactiveItem flag, which
 * only exists on CI_Item/its RDS mirror — neither itemCatalogue nor
 * itemCatalogueAltitude carries it (see productPricing.ts's getCatalog for
 * the join). Exactly 2 states, no "all" — day-to-day work is active-only,
 * inactive is for historical lookup. Defaults to Active.
 */
export default class ProductPricing extends Component {
    state = {
        companies: [],
        products: [],
        // Product lines on the CURRENT page, in display order, each with
        // {productLine, productLineDesc, itemCount} — grouping (2026-08-20,
        // client's ask). `products` above holds every item across ALL these
        // lines (never truncated mid-line, see getCatalog's header comment).
        lines: [],
        // pricing keyed by `${itemCode}__${idCompany}` — one entry per existing
        // product_pricing row, from the same /v3/pricing/catalog response as
        // `products` (single round-trip for the whole page of products).
        pricing: {},
        // Local input state per company sub-row, same key shape as `pricing`,
        // seeded from `pricing` on expand and updated as the admin types —
        // kept separate from `pricing` so typing doesn't require a re-fetch.
        edits: {},
        expanded: {}, // itemCode -> true when expanded
        saving: {}, // key -> true while that row's save is in flight
        search: '',
        // Active/Inactive filter (2026-08-19) — exactly 2 states, no "all"
        // option (client's ask: day-to-day work is active-only, inactive is
        // for historical lookup). Defaults to Active.
        statusFilter: 'active',
        // Product line filter (2026-08-20, client's ask — audit one line at a
        // time). '' means "all lines". Options come from a dedicated endpoint
        // (not from the current page's own `lines`) so the dropdown always
        // lists every line, not just whatever happens to be on this page.
        productLineFilter: '',
        productLineOptions: [],
        page: 1,
        // pageSize now counts PRODUCT LINES, not items (2026-08-20) — matches
        // getCatalog's own default and cap.
        pageSize: 10,
        total: 0,
        loading: false,
        loaded: false,
        errorMessage: '',
        // Company pricing hierarchy (2026-08-19) — idCompany currently saving
        // a "Buys from" change, so its select can show a spinner/disable.
        savingParentFor: null,
        showCompanySettings: false,
        // Enlarge-photo modal (2026-08-20) — the itemURLImage currently shown full-size, or null.
        imageModalUrl: null,
    }

    isAdmin = getValueCookie('TypeUser') === '1'

    componentDidMount() {
        this.fetchCompanies()
        this.fetchCatalog()
        this.fetchProductLineOptions()
    }

    async fetchCompanies() {
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/companies', {})
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ companies: resp.data })
            }
        } catch (error) {
            console.error('fetchCompanies failed:', error)
        }
    }

    /** Populates the product line filter's options — independent of search/page, tied only to the active/inactive status filter. */
    async fetchProductLineOptions() {
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/catalog/lines', { status: this.state.statusFilter })
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ productLineOptions: resp.data })
            }
        } catch (error) {
            console.error('fetchProductLineOptions failed:', error)
        }
    }

    /** Sets which company a distributor buys from (2026-08-19, company pricing hierarchy). */
    async setParentCompany(idCompany, parentCompanyId) {
        this.setState({ savingParentFor: idCompany })
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/companies/set-parent', { idCompany, parentCompanyId })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ savingParentFor: null, errorMessage: (resp && resp.message) || 'Could not update the company relationship.' })
                return
            }
            this.setState((prev) => ({
                savingParentFor: null,
                companies: prev.companies.map((c) => c.idCompany === idCompany ? { ...c, parentCompanyId } : c),
            }))
        } catch (error) {
            this.setState({ savingParentFor: null, errorMessage: error.message || 'Error updating the company relationship.' })
        }
    }

    pricingKey(itemCode, idCompany) {
        return itemCode + '__' + idCompany
    }

    /** All prices here are dollars/cents — 2 decimals everywhere (2026-08-19). '' stays ''. */
    fmt2(v) {
        if (v === '' || v === null || v === undefined || !isFinite(Number(v))) return v === '' || v === null || v === undefined ? '' : v
        return Number(v).toFixed(2)
    }

    async fetchCatalog() {
        this.setState({ loading: true, errorMessage: '' })
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/catalog', {
                search: this.state.search || undefined,
                status: this.state.statusFilter,
                productLine: this.state.productLineFilter || undefined,
                page: this.state.page,
                pageSize: this.state.pageSize,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ loading: false, loaded: true, errorMessage: 'Could not load the product catalog.' })
                return
            }
            const products = (resp.data && Array.isArray(resp.data.products)) ? resp.data.products : []
            const pricingRows = (resp.data && Array.isArray(resp.data.pricing)) ? resp.data.pricing : []
            const lines = (resp.data && Array.isArray(resp.data.lines)) ? resp.data.lines : []
            const pricing = {}
            for (const row of pricingRows) {
                pricing[this.pricingKey(row.itemCode, row.idCompany)] = row
            }
            this.setState({ loading: false, loaded: true, products, pricing, lines, total: resp.total || lines.length })
        } catch (error) {
            this.setState({ loading: false, loaded: true, errorMessage: error.message || 'Error loading the catalog.' })
        }
    }

    toggleExpand(product) {
        const itemCode = product.itemCode
        const isExpanding = !this.state.expanded[itemCode]
        this.setState((prev) => ({ expanded: { ...prev.expanded, [itemCode]: isExpanding } }))

        if (!isExpanding) return
        // Seed local edit inputs for every ACTIVE company from whatever pricing
        // already exists (or empty, for a company with no row yet) — this is
        // what makes each sub-row a ready-to-save upsert form the moment it's visible.
        this.setState((prev) => {
            const edits = { ...prev.edits }
            for (const company of prev.companies) {
                const key = this.pricingKey(itemCode, company.idCompany)
                if (edits[key]) continue
                const existing = prev.pricing[key]
                // Cost pre-fills from the existing row if there is one, otherwise
                // from the synced catalog value as a starting suggestion — either
                // way it's editable, since not every item has a synced cost yet.
                const suggestedCost = (existing && existing.cost !== null && existing.cost !== undefined)
                    ? existing.cost
                    : (product.standardUnitCost !== null && product.standardUnitCost !== undefined ? product.standardUnitCost : '')
                edits[key] = {
                    // fmt2 (2026-08-19) — DECIMAL(15,4) columns come back from
                    // mysql2 as 4-decimal strings ("13.0000"); trim to 2 for
                    // display, matching what everything here actually means (dollars/cents).
                    cost: this.fmt2(suggestedCost),
                    // standardPrice is kept (not shown in the form anymore — see
                    // header comment) purely so saveCompanyRow can send it back
                    // unchanged; upsert() nulls out any field it doesn't receive,
                    // and getMap/getCatalog still fall back to this column for
                    // items whose level prices aren't filled in yet.
                    standardPrice: (existing && existing.standardPrice !== null && existing.standardPrice !== undefined) ? existing.standardPrice : '',
                    minPrice: (existing && existing.minPrice !== null && existing.minPrice !== undefined) ? existing.minPrice : '',
                    maxPrice: (existing && existing.maxPrice !== null && existing.maxPrice !== undefined) ? existing.maxPrice : '',
                    level1Price: this.fmt2((existing && existing.level1Price !== null && existing.level1Price !== undefined) ? existing.level1Price : ''),
                    level2Price: this.fmt2((existing && existing.level2Price !== null && existing.level2Price !== undefined) ? existing.level2Price : ''),
                    level3Price: this.fmt2((existing && existing.level3Price !== null && existing.level3Price !== undefined) ? existing.level3Price : ''),
                    // Level 3 below-minimum exception (2026-08-20, migration 017) —
                    // internal arrangement to sell Level 3 at cost for this one
                    // product+company. Only affects Level 3's floor, see
                    // productPricing.ts's validatePricingRules.
                    allowLevel3BelowCost: !!(existing && existing.allowLevel3BelowCost),
                }
            }
            return { edits }
        })
    }

    setEditValue(itemCode, idCompany, field, value) {
        const key = this.pricingKey(itemCode, idCompany)
        this.setState((prev) => ({
            edits: {
                ...prev.edits,
                [key]: { ...prev.edits[key], [field]: value },
            },
        }))
    }

    /**
     * Company pricing hierarchy (2026-08-19): a company with no parentCompanyId
     * is a "master" (Mayaland) — Cost stays admin-editable, Min is always
     * cost * 1.05. A company WITH a parentCompanyId (e.g. Altitude) is a
     * "distributor" — Cost = master's Min, Min = master's Level 1, neither
     * editable. Computed LIVE from the master's currently-typed edit values
     * (not its last-saved ones), matching the actual server-side rule in
     * productPricing.ts's upsert exactly — only stale between a real edit and
     * the next keystroke, never between page loads.
     */
    getComputedCostMin(product, company) {
        const toNum = (v) => (v !== '' && v !== null && v !== undefined && isFinite(Number(v))) ? Number(v) : null

        if (!company.parentCompanyId) {
            const edit = this.state.edits[this.pricingKey(product.itemCode, company.idCompany)] || {}
            const cost = toNum(edit.cost)
            const minPrice = cost !== null ? Math.round(cost * 1.05 * 100) / 100 : null
            return { cost, costEditable: true, minPrice, missingParent: false }
        }

        const parentEdit = this.state.edits[this.pricingKey(product.itemCode, company.parentCompanyId)] || {}
        const parentCost = toNum(parentEdit.cost)
        const parentMin = parentCost !== null ? Math.round(parentCost * 1.05 * 100) / 100 : null
        const parentLevel1 = toNum(parentEdit.level1Price)
        return { cost: parentMin, costEditable: false, minPrice: parentLevel1, missingParent: parentCost === null || parentLevel1 === null }
    }

    async saveCompanyRow(product, idCompany) {
        const key = this.pricingKey(product.itemCode, idCompany)
        const edit = this.state.edits[key] || { cost: '', standardPrice: '', minPrice: '', maxPrice: '', level1Price: '', level2Price: '', level3Price: '', allowLevel3BelowCost: false }
        this.setState((prev) => ({ saving: { ...prev.saving, [key]: true } }))
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/upsert', {
                itemCode: product.itemCode,
                idCompany,
                cost: edit.cost,
                standardPrice: edit.standardPrice,
                minPrice: edit.minPrice,
                maxPrice: edit.maxPrice,
                level1Price: edit.level1Price,
                level2Price: edit.level2Price,
                level3Price: edit.level3Price,
                allowLevel3BelowCost: !!edit.allowLevel3BelowCost,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                // resp.message carries the specific pricing-rule violation
                // (e.g. "Level 2 must be lower than Level 1") once CABE.js's
                // getInformationWithDataV3 actually forwards it — see that
                // file's 2026-08-19 fix.
                const detail = resp && resp.message ? resp.message : null
                this.setState((prev) => ({ saving: { ...prev.saving, [key]: false }, errorMessage: detail || ('Could not save ' + product.itemCode + '.') }))
                return
            }
            // Patch just this row into `pricing` — avoids re-fetching the whole
            // page just to reflect one save, and keeps completeness live.
            this.setState((prev) => ({
                saving: { ...prev.saving, [key]: false },
                pricing: { ...prev.pricing, [key]: resp.data },
            }))
        } catch (error) {
            this.setState((prev) => ({ saving: { ...prev.saving, [key]: false }, errorMessage: error.message || 'Error saving.' }))
        }
    }

    // Completeness across companies — drives both the product-row indicator and
    // the sub-row background. This IS the "how do I get started" answer: nothing
    // needs a separate seed step, expand any product and fill in what's missing.
    completeness(itemCode) {
        const { companies, pricing } = this.state
        if (companies.length === 0) return { state: 'unknown', configured: 0, missing: 0, total: 0 }
        let configured = 0
        for (const company of companies) {
            if (pricing[this.pricingKey(itemCode, company.idCompany)]) configured++
        }
        const total = companies.length
        const missing = total - configured
        const state = configured === 0 ? 'none' : configured < total ? 'partial' : 'full'
        return { state, configured, missing, total }
    }

    rowBackground(completenessState) {
        if (completenessState === 'none') return 'rgba(239,68,68,0.06)'
        if (completenessState === 'partial') return 'rgba(245,158,11,0.08)'
        return '#F8FAFC'
    }

    // Icon + label badge — same visual language used on the sub-rows, now also
    // on the product row itself so completeness is visible without expanding.
    completenessBadge(completenessInfo) {
        const { state, missing } = completenessInfo
        if (state === 'none') {
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#991B1B', fontWeight: 700, fontSize: '0.78rem' }}>
                <AiOutlineWarning /> Not configured
            </span>
        }
        if (state === 'partial') {
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#92400E', fontWeight: 700, fontSize: '0.78rem' }}>
                <AiOutlineWarning /> Missing {missing}
            </span>
        }
        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#065F46', fontWeight: 700, fontSize: '0.78rem' }}>
            <AiOutlineCheckCircle /> Configured
        </span>
    }

    marginPreview(standardPrice, cost) {
        const sp = Number(standardPrice)
        const c = Number(cost)
        if (!sp || sp <= 0 || !isFinite(c)) return null
        return Math.round(((sp - c) / sp) * 10000) / 100
    }

    marginStyle(margin) {
        if (margin === null || margin === undefined) return { color: '#64748B', background: 'transparent', border: '1px solid #E2E8F0' }
        if (margin < LOW_MARGIN_THRESHOLD) return { color: '#991B1B', background: 'rgba(239,68,68,0.08)', border: '1px solid #FECACA' }
        return { color: '#065F46', background: 'rgba(16,185,129,0.08)', border: '1px solid #A7F3D0' }
    }

    /**
     * Per-level margin badge (2026-08-19) — replaces the single margin column
     * that used to sit next to Min/Max, now one under each Level input since
     * every tier has its own price and therefore its own margin. Renders
     * nothing when the level has no price yet (nothing to compute).
     */
    marginBadge(margin) {
        if (margin === null || margin === undefined) return null
        const mStyle = this.marginStyle(margin)
        return (
            <span style={{ ...mStyle, borderRadius: '6px', padding: '2px 6px', fontSize: '0.7rem', fontWeight: 700, display: 'inline-block', marginTop: '4px' }}>
                {margin}%
            </span>
        )
    }

    render() {
        const { products, lines, companies, pricing, edits, expanded, saving, loading, errorMessage, search, statusFilter, productLineFilter, productLineOptions, page, pageSize, total, showCompanySettings, savingParentFor, imageModalUrl } = this.state
        // Group products by product line, in the order `lines` came back
        // (2026-08-20) — one map build per render, cheap at this page's size.
        const productsByLine = {}
        for (const p of products) {
            const key = p.productLine || ''
            if (!productsByLine[key]) productsByLine[key] = []
            productsByLine[key].push(p)
        }
        const totalPages = Math.max(1, Math.ceil(total / pageSize))

        return (
            <div className="dash-page" style={{ paddingTop: '30px' }}>
                <Menu />

                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '2rem 2rem' }}>
                    <div style={{ flex: 1 }}>
                        <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.5rem', margin: 0 }}>Price Management</h1>
                        <p style={{ color: 'rgba(255,255,255,0.65)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                            Click a product to configure its price per company
                        </p>
                    </div>
                </div>

                <div style={{ padding: '1.5rem 2rem' }}>
                    {/* Company pricing hierarchy (2026-08-19) — who buys from whom.
                        Collapsed by default: a rarely-touched setup step, not
                        something the admin needs open during day-to-day pricing. */}
                    <div className="card mb-3">
                        <div className="card-body" style={{ padding: '0.85rem 1.25rem' }}>
                            <button onClick={() => this.setState({ showCompanySettings: !showCompanySettings })}
                                style={{ background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#0F172A', fontWeight: 700, fontSize: '0.85rem' }}>
                                {showCompanySettings ? <AiOutlineDown /> : <AiOutlineRight />} Company Settings
                            </button>
                            {showCompanySettings && (
                                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                    <p style={{ fontSize: '0.78rem', color: '#64748B', margin: 0 }}>
                                        A company with no parent is a master (its own Cost/Level 1). A company that buys from another has its Cost and Min derived automatically.
                                    </p>
                                    {companies.map((company) => (
                                        <div key={company.idCompany} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <div style={{ minWidth: '160px', fontWeight: 600, fontSize: '0.85rem' }}>{company.name}</div>
                                            <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Buys from:</span>
                                            <select className="form-select form-select-sm" style={{ width: '220px' }}
                                                disabled={!this.isAdmin || savingParentFor === company.idCompany}
                                                value={company.parentCompanyId || ''}
                                                onChange={(e) => this.setParentCompany(company.idCompany, e.target.value ? Number(e.target.value) : null)}>
                                                <option value="">None (master company)</option>
                                                {companies.filter((c) => c.idCompany !== company.idCompany).map((c) => (
                                                    <option key={c.idCompany} value={c.idCompany}>{c.name}</option>
                                                ))}
                                            </select>
                                            {savingParentFor === company.idCompany && <span style={{ fontSize: '0.78rem', color: '#94A3B8' }}>Saving...</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div style={{ flex: 1, minWidth: '220px' }}>
                                <div style={{ position: 'relative' }}>
                                    <AiOutlineSearch style={{ position: 'absolute', left: '10px', top: '10px', color: '#94A3B8' }} />
                                    <input type="text" className="form-control" style={{ paddingLeft: '32px' }}
                                        placeholder="Search item code or description..." value={search}
                                        onChange={(e) => this.setState({ search: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') this.setState({ page: 1 }, () => this.fetchCatalog()) }} />
                                </div>
                            </div>
                            {/* Active/Inactive filter (2026-08-19) — exactly 2 states, default Active */}
                            <div style={{ display: 'inline-flex', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
                                {['active', 'inactive'].map((s) => (
                                    <button key={s}
                                        onClick={() => this.setState({ statusFilter: s, productLineFilter: '', page: 1 }, () => { this.fetchCatalog(); this.fetchProductLineOptions() })}
                                        style={{
                                            border: 'none', padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                                            background: statusFilter === s ? '#2563EB' : '#FFFFFF',
                                            color: statusFilter === s ? '#FFFFFF' : '#64748B',
                                        }}>
                                        {s === 'active' ? 'Active' : 'Inactive'}
                                    </button>
                                ))}
                            </div>
                            {/* Product line filter (2026-08-20) — audit one line at a time */}
                            <div style={{ minWidth: '220px' }}>
                                <select className="form-select" value={productLineFilter}
                                    onChange={(e) => this.setState({ productLineFilter: e.target.value, page: 1 }, () => this.fetchCatalog())}>
                                    <option value="">All product lines</option>
                                    {productLineOptions.map((l) => (
                                        <option key={l.productLine || '(none)'} value={l.productLine || ''}>
                                            {(l.productLineDesc || l.productLine || '(No product line)') + ' (' + l.itemCount + ')'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button style={BTN_PRIMARY} onClick={() => this.setState({ page: 1 }, () => this.fetchCatalog())}>Search</button>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="metric-card status-danger mb-3" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
                            <div style={{ color: '#DC2626', fontSize: '0.85rem' }}>{errorMessage}</div>
                            <button onClick={() => this.setState({ errorMessage: '' })}
                                style={{ background: 'none', border: 'none', padding: 0, color: '#DC2626', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                                aria-label="Dismiss">
                                <AiOutlineClose />
                            </button>
                        </div>
                    )}

                    <div className="card">
                        <div className="tableFixHeadSmall">
                            <table className="table table-clean">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Photo</th>
                                        <th>Item Code</th>
                                        <th>Description</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.length === 0 && !loading && (
                                        <tr><td colSpan={5} className="empty">No results</td></tr>
                                    )}
                                    {lines.map((line) => (
                                        <React.Fragment key={line.productLine || '(none)'}>
                                            {/* Group header (2026-08-20) — spans the full table, one per
                                                product line, so the client can review a whole line at a
                                                glance. itemCount is the group's own total, not affected
                                                by pagination since a line is never split across pages. */}
                                            <tr style={{ background: '#F1F5F9' }}>
                                                <td colSpan={5} style={{ fontWeight: 800, fontSize: '0.8rem', color: '#334155', padding: '8px 12px' }}>
                                                    {line.productLineDesc || line.productLine || '(No product line)'}
                                                    <span style={{ marginLeft: '8px', fontWeight: 600, fontSize: '0.72rem', color: '#64748B' }}>
                                                        {line.itemCount} product{line.itemCount === 1 ? '' : 's'}
                                                    </span>
                                                </td>
                                            </tr>
                                            {(productsByLine[line.productLine || ''] || []).map((product) => {
                                        const isOpen = !!expanded[product.itemCode]
                                        const completenessInfo = this.completeness(product.itemCode)
                                        return (
                                            <React.Fragment key={product.itemCode}>
                                                <tr onClick={() => this.toggleExpand(product)} style={{ background: this.rowBackground(completenessInfo.state), cursor: 'pointer' }}>
                                                    <td style={{ width: '32px', textAlign: 'center' }}>{isOpen ? <AiOutlineDown /> : <AiOutlineRight />}</td>
                                                    <td style={{ width: '52px' }}>
                                                        {product.itemURLImage ? (
                                                            <img src={product.itemURLImage} alt={product.itemCode}
                                                                style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '6px', background: '#F8FAFC', border: '1px solid #F1F5F9', padding: '2px', cursor: 'zoom-in' }}
                                                                onClick={(e) => { e.stopPropagation(); this.setState({ imageModalUrl: product.itemURLImage }) }} />
                                                        ) : (
                                                            <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#F1F5F9', border: '1px solid #E2E8F0' }} />
                                                        )}
                                                    </td>
                                                    <td className="mono">{product.itemCode}</td>
                                                    {/* Long description (2026-08-20, client's ask) — itemCatalogue's own
                                                        `description` is short and misses details like mL/size; falls
                                                        back to it only when CI_Item's long one isn't available. */}
                                                    <td className="small">{product.longDescription || product.description || '—'}</td>
                                                    <td>{this.completenessBadge(completenessInfo)}</td>
                                                </tr>
                                                {isOpen && companies.map((company) => {
                                                    const key = this.pricingKey(product.itemCode, company.idCompany)
                                                    const existing = pricing[key]
                                                    const edit = edits[key] || { cost: '', standardPrice: '', minPrice: '', maxPrice: '', level1Price: '', level2Price: '', level3Price: '' }
                                                    // Company pricing hierarchy (2026-08-19) — Cost/Min are computed LIVE
                                                    // from whatever's currently typed (master: cost*1.05; distributor:
                                                    // derived from the master's own row), not from what was last saved,
                                                    // so editing Mayaland's cost updates Altitude's preview immediately.
                                                    const computed = this.getComputedCostMin(product, company)
                                                    const margin1 = this.marginPreview(edit.level1Price, computed.cost)
                                                    const margin2 = this.marginPreview(edit.level2Price, computed.cost)
                                                    const margin3 = this.marginPreview(edit.level3Price, computed.cost)
                                                    const isSaving = !!saving[key]
                                                    return (
                                                        <tr key={key} style={{ background: '#FFFFFF' }}>
                                                            <td></td>
                                                            <td colSpan={4}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', padding: '6px 0 6px 24px', borderLeft: '3px solid ' + (existing ? '#A7F3D0' : '#FECACA') }}>
                                                                    <div style={{ minWidth: '300px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem', fontWeight: 700, fontSize: '0.85rem' }}>
                                                                        {existing ? <AiOutlineCheckCircle style={{ color: '#059669' }} /> : <AiOutlineWarning style={{ color: '#DC2626' }} />}
                                                                        {company.name}
                                                                        {/* Always-visible relationship badge (2026-08-19) — not just when
                                                                            it's a distributor, so "buys from nobody" (master) is just as
                                                                            obvious without opening Company Settings. */}
                                                                        {company.parentCompanyId ? (
                                                                            <p style={{ fontSize: '0.65rem', width: '100%', fontWeight: 700, color: '#5b21b6', background: 'rgba(139,92,246,0.1)', border: '1px solid #DDD6FE', borderRadius: '999px', padding: '2px 8px' }}>
                                                                                buys from {(companies.find((c) => c.idCompany === company.parentCompanyId) || {}).name || `#${company.parentCompanyId}`}
                                                                            </p>
                                                                        ) : (
                                                                            <p style={{ fontSize: '0.65rem', width: '100%', fontWeight: 700, color: '#1e40af', background: 'rgba(37,99,235,0.08)', border: '1px solid #BFDBFE', borderRadius: '999px', padding: '2px 8px' }}>
                                                                                master
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    {computed.missingParent && (
                                                                        <div style={{ fontSize: '0.72rem', color: '#991B1B', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                            <AiOutlineWarning /> Configure the parent company's Cost and Level 1 first
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Cost{!computed.costEditable && ' (auto)'}</div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin || !computed.costEditable}
                                                                            className="form-control form-control-sm" style={{ width: '90px', background: computed.costEditable ? undefined : '#F1F5F9' }}
                                                                            value={computed.costEditable ? edit.cost : (computed.cost === null ? '' : computed.cost.toFixed(2))}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'cost', e.target.value)} />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Level 1 <span style={{ color: '#94A3B8', fontWeight: 400 }}>(default)</span></div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '100px' }}
                                                                            value={edit.level1Price}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'level1Price', e.target.value)} />
                                                                        {this.marginBadge(margin1)}
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Level 2</div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '100px' }}
                                                                            value={edit.level2Price}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'level2Price', e.target.value)} />
                                                                        {this.marginBadge(margin2)}
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Level 3</div>
                                                                        <input type="number" step="0.01" disabled={!this.isAdmin} className="form-control form-control-sm" style={{ width: '100px' }}
                                                                            value={edit.level3Price}
                                                                            onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'level3Price', e.target.value)} />
                                                                        {this.marginBadge(margin3)}
                                                                        {/* Level 3 below-minimum exception (2026-08-20, migration 017) —
                                                                            special-case internal arrangement, only affects Level 3's own
                                                                            floor (minPrice -> cost). Level 1/2 never affected. */}
                                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '0.68rem', color: '#64748B', cursor: this.isAdmin ? 'pointer' : 'default' }}>
                                                                            <input type="checkbox" disabled={!this.isAdmin} checked={!!edit.allowLevel3BelowCost}
                                                                                onChange={(e) => this.setEditValue(product.itemCode, company.idCompany, 'allowLevel3BelowCost', e.target.checked)} />
                                                                            Allow below min (to cost)
                                                                        </label>
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.68rem', color: '#94A3B8' }}>Min (auto)</div>
                                                                        <input type="number" step="0.01" disabled className="form-control form-control-sm" style={{ width: '90px', background: '#F1F5F9' }}
                                                                            value={computed.minPrice === null ? '' : computed.minPrice.toFixed(2)} />
                                                                    </div>
                                                                    {this.isAdmin && (
                                                                        <button style={{ ...BTN_PRIMARY, padding: '6px 14px', fontSize: '0.78rem' }}
                                                                            disabled={isSaving || computed.missingParent} onClick={() => this.saveCompanyRow(product, company.idCompany)}>
                                                                            {isSaving ? 'Saving...' : 'Save'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </React.Fragment>
                                        )
                                    })}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', padding: '12px 16px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Page {page} of {totalPages} ({total} product lines)</span>
                            <button style={{ ...BTN_SECONDARY, opacity: page <= 1 ? 0.5 : 1 }}
                                disabled={page <= 1} onClick={() => this.setState({ page: page - 1 }, () => this.fetchCatalog())}>Previous</button>
                            <button style={{ ...BTN_SECONDARY, opacity: page >= totalPages ? 0.5 : 1 }}
                                disabled={page >= totalPages} onClick={() => this.setState({ page: page + 1 }, () => this.fetchCatalog())}>Next</button>
                        </div>
                    </div>
                </div>
                {imageModalUrl && (
                    <div onClick={() => this.setState({ imageModalUrl: null })}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div onClick={(e) => e.stopPropagation()} style={{ background: '#FFFFFF', borderRadius: '12px', padding: '1.25rem', maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }}>
                            <button onClick={() => this.setState({ imageModalUrl: null })}
                                style={{ position: 'absolute', top: '-14px', right: '-14px', width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>
                                ✕
                            </button>
                            <img src={imageModalUrl} alt="" style={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
                        </div>
                    </div>
                )}
            </div>
        )
    }
}
